import { MRT_ColumnDef } from "material-react-table";
import {DB_PROFILE_KEY_TO_DISPLAY_NAME, IFetchObjectsResponse, IFullObjectData, IProcessedObjectData, TAnyRwoObject, TPmoAndMiscProfileNames} from "../../constants/objects/ObjectTypes/common";
import { RealWorldObjectRow } from "../../containers/pages/objectApp/constants";
import {roundToNDecimals} from "../helpers";
import { COLUMN_NAMES } from "../../containers/pages/realWorldObjects/constants";
import { findObjectDataByVariantId } from "../../containers/components/Dialogs/RedlineAppDialogs/EditPropertyDialog";

const getProfileDisplayName = (profileKey: TPmoAndMiscProfileNames): string => {
  return DB_PROFILE_KEY_TO_DISPLAY_NAME[profileKey] ?? profileKey;
}
/**
 * Process the object data fetched from the server. For now, just process rwo.
 * @param {IFetchObjectsResponse} fetchObjectsResponse - The response from the server containing the object data.
 * @returns {IProcessedObjectData} - The processed object data.
 */
const processObjectData = (fetchObjectsResponse: IFetchObjectsResponse): IProcessedObjectData => {
  const processedData: IProcessedObjectData = {};

  const addObjectToProcessedData = (object: TAnyRwoObject, category: string, name: string, version: number | null, variants: { [variant: string]: string }) => {
    if(!version) {
      return;
    }
    if (!processedData[category]) {
      processedData[category] = {};
    }
    if (!processedData[category][name]) {
      processedData[category][name] = {};
    }
    processedData[category][name][version] = {
      object,
      variants,
    };
  };

  fetchObjectsResponse.rwoData.forEach((object) => {
    // Get category
    const category = object.type;

    // Get name
    let name;
    if (object.type === "Miscellaneous" || object.type === "Potentially Moving Object") {
      name = object.objectName;
    } else if (object.type === "Sign") {
      name = object.signName;
    }
    if (!name) {
      return;
    }

    // Get version
    const version = object.latestVersion;
    if (!version) {
      return;
    }

    // Get variants
    let variants: { [variant: string]: string } = {};
    if (object.type === "Miscellaneous" || object.type === "Potentially Moving Object") {
      Object.keys(object.parameters).forEach((variant) => {
        if (variant !== "general" && object.parameters[variant as TPmoAndMiscProfileNames].id) {
          variants[variant] = object.parameters[variant as TPmoAndMiscProfileNames].id;
        }
      });
    } else if (object.type === "Sign") {
      object.signVariants.forEach((variant) => {
        if (variant.id) {
          variants[getSignVariantNameFromWidthHeight(variant.width, variant.height)] = variant.id;
        }
      });
    }
    if (Object.keys(variants).length === 0) {
      return;
    }

    addObjectToProcessedData(object, category, name, version, variants);
  });

  return processedData;
};

/**
 * Get the name of a sign variant from its width and height.
 * @param {number | undefined} width - The width of the sign variant.
 * @param {number | undefined} height - The height of the sign variant.
 * @returns {string} - The name of the sign variant, or empty if the width or height are not a number.
 */
const getSignVariantNameFromWidthHeight = (width: number | undefined, height: number | undefined): string => {
  if (width == null || height == null || isNaN(width) || isNaN(height) || typeof width !== 'number' || typeof height !== 'number') {
    return '';
  }
  return `${roundToNDecimals(width, 3)} x ${roundToNDecimals(height, 3)}`;
}
/**
 * This function filters the items object to only include keys that match the headers of the visible columns.
 * @param items 
 * @param visibleColumns 
 * @returns  {Record<string, any>} - Filtered items based on visible columns.
 */
const filterVisibleColumns = (items: Record<string, any>, visibleColumns: MRT_ColumnDef<RealWorldObjectRow>[]) => {
  // Filter the items object to include only keys that match the headers of the visible columns.
  // Special case mappings:
  // - "Images" maps to "Image Thumbnail" to handle display transformations for image-related data.
  // - COLUMN_NAMES.OBJECT_NAME_PROFILE maps to "Object Name" for consistency in column headers.
  return Object.fromEntries(Object.entries(items).filter(([key]) =>
    visibleColumns.some(col => col.header === key || (key === "Images" && col.header === "Image Thumbnail") || (key === COLUMN_NAMES.OBJECT_NAME_PROFILE && col.header === "Object Name"))
  ));
}
/**
 * Filters the real world objects table columns based on their visibility and accessor key.
 * @param realWorldObjectsTableColumns - Array of table columns.
 * @param columnVisibility - Object representing the visibility of columns.
 * @returns {MRT_ColumnDef<RealWorldObjectRow>[]} - Filtered table columns.
 */
const getVisibleTableColumns = (
  realWorldObjectsTableColumns: MRT_ColumnDef<RealWorldObjectRow>[],
  columnVisibility: Record<string, boolean>
): MRT_ColumnDef<RealWorldObjectRow>[] => {
  return realWorldObjectsTableColumns.filter(
    (col: MRT_ColumnDef<RealWorldObjectRow>) => {
      const key = col.accessorKey || col.id;
      return key && key !== "select" && columnVisibility[key] !== false;
    }
  );
};
/**
 * Removes columns from the filtered object that are not visible based on the provided keys.
 * @param filteredObj - The object to filter.
 * @param {string[]} columnVisibilityFalseKeys - Array of keys representing columns that are not visible.
 * @returns {void} - The function modifies the filteredObj in place.
 */
const removeHiddenColumns = (filteredObj: any, columnVisibilityFalseKeys: string[]) => {
  columnVisibilityFalseKeys.forEach((key) => {
    if (key in filteredObj) {
      delete filteredObj[key];
    }
  });
}
/**
 * Sanitizes undefined values in the filtered sub-row object by replacing them with an empty string.
 * This is useful for ensuring that undefined values when set to empty string will pass into the payload http request.
 * @param filteredSubRow - The object to sanitize.
 * @return {void} - The function modifies the filteredSubRow in place.
 */
const sanitizeUndefinedValues = (filteredSubRow: Record<string, any>) => {
  Object.keys(filteredSubRow).forEach((key) => {
    if (filteredSubRow[key] === undefined) {
      filteredSubRow[key] = "";
    }
  });
}
/**
 * Maps headers to keys in the row object based on the column definitions.
 * This function iterates through the columns and updates the row object by replacing keys with their corresponding headers.
 * @param columns - Array of column definitions.
 * @param row - The row object to map headers to keys.
 * @return {void} - The function modifies the row object in place.
 */
const mapHeadersToKeys = (columns: any[], row: any) => {
  columns.forEach(({ header, accessorKey, id }: any) => {
    const key = accessorKey || id;
    if (key && key in row) {
      row[header] = row[key];
      delete row[key];
    }
  });
};
/** 
  * Gets the version display string for an object given its ID and processed object data.
  * If the object ID is undefined or null, it returns an empty string.
  * If the version is found, it returns a string in the format "v{version} ".
  * @param {string | undefined | null} objId - The ID of the object.
  * @param {IProcessedObjectData} objCatToData - The processed object data.
  * @returns {string} - The version display string.
  */
const getObjVersionDisplayString = (objId: string | undefined | null, objCatToData: IProcessedObjectData): string => {
  // Get the version from the object data given the object ID
  const _fullObjectData: IFullObjectData | null = objId ? findObjectDataByVariantId(objId, objCatToData) : null;
  const version = _fullObjectData?.fullObjectData?.latestVersion ?? null;
  let versionString: string = "";
  if (version !== null) {
    versionString = "v" + String(version) + " ";
  }
  return versionString;
};
/**
 * Sorts the keys of the processed object data and its inner objects in a case-insensitive manner.
 * @param processedObjectData - The processed object data to be sorted.
 * This function sorts the keys of the processed object data and its inner objects in a case-insensitive manner.
 * @returns {IProcessedObjectData} - The sorted processed object data.
 */
const sortObjectData = (processedObjectData: IProcessedObjectData): IProcessedObjectData => {
  return Object.keys(processedObjectData)
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
    .reduce((acc, key) => {
      acc[key] = Object.keys(processedObjectData[key])
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
        .reduce((innerAcc, innerKey) => {
          innerAcc[innerKey] = processedObjectData[key][innerKey];
          return innerAcc;
        }, {} as IProcessedObjectData[string]);
      return acc;
    }, {} as IProcessedObjectData);
};

export { getProfileDisplayName, processObjectData, getSignVariantNameFromWidthHeight, filterVisibleColumns, getVisibleTableColumns, removeHiddenColumns, sanitizeUndefinedValues, mapHeadersToKeys, sortObjectData, getObjVersionDisplayString};